/* eslint-disable @typescript-eslint/unbound-method */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ConfigConstants, ConfigService, Notification, isDefined } from '@evorait/pace-core';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { DocBoxService } from '../../../services/docBox.service';
import {
    DocBoxReportStatus,
    DocumentFormStateUpload,
    DocumentState,
    DocumentStateUpload,
} from '../../../store/state/document.state';
import { FormsConstants } from '../../../types/forms-constants';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

export function allowedUploadMIMETypesValidator(allowedTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const file = control.value as File;
        const allowed = allowedTypes.includes(file.type);

        return allowed ? null : { forbiddenFileType: { value: file.type } };
    };
}

@Component({
    selector: 'pace-forms-upload-modal',
    templateUrl: './upload-modal.component.html',
    styleUrls: ['./upload-modal.component.scss'],
})
export class UploadModalComponent implements OnInit, OnDestroy {
    public formGroup: FormGroup;
    public readonly allowedMimeTypes: string[];

    public readonly reportStatusEnums = [
        { key: FormsConstants.INTERIM, value: FormsConstants.INTERIM },
        { key: FormsConstants.FINAL, value: FormsConstants.FINAL },
    ];

    public readonly docTypeOptions: Array<{ key: string; value: string }>;
    public readonly protectionClasses = [
        { key: FormsConstants.INTERNAL, value: FormsConstants.INTERNAL },
        { key: FormsConstants.CUSTOMER_VISIBLE, value: FormsConstants.CUSTOMER_VISIBLE },
    ];

    public uploadPending = false;
    public showErrorMessage = false;
    public showSuccessMessage = false;
    public isStatusMandatory = true;
    public readonly formControlInvalid = ConfigConstants.formInvalid;
    public faCheck = faCheck;

    @Input() public docBoxModel: DocumentState;
    @Input() public notification: Notification;

    public modalResult = new Subject<string>(); // This is to emit the uploaded document's title which is later displayed on summary screen of pace-confirmation
    private onDestroy$: Subject<void> = new Subject();
    public constructor(
        private readonly modalInstance: BsModalRef,
        private docBoxService: DocBoxService,
        private configService: ConfigService,
    ) {
        this.allowedMimeTypes = this.configService.getUploadToDocBoxAllowedMimeTypes();
        // GW expects the as submitted value the display value of the dropdown
        this.docTypeOptions = this.configService.getDocboxDoctypes().map((doctype) => {
            return {
                key: doctype.value,
                value: doctype.value,
            };
        });
        this.isStatusMandatory = this.configService.getUploadToDocBoxStatusMandatory();
    }

    public ngOnInit(): void {
        const titleMaxlength = 256;
        this.formGroup = new FormGroup({
            file: new FormControl('', [Validators.required, allowedUploadMIMETypesValidator(this.allowedMimeTypes)]),
            docType: new FormControl('', Validators.required),
            title: new FormControl('', [Validators.required, Validators.maxLength(titleMaxlength)]),
            date: new FormControl(moment().format('YYYY-MM-DD')),
            orderNumber: new FormControl(),
            reportStatus: new FormControl('', this.isStatusMandatory ? Validators.required : undefined),
            protectionClass: new FormControl({ value: '', disabled: true }, Validators.required),
            phiAgreement: new FormControl(false, Validators.requiredTrue),
        });

        if (isDefined(this.docBoxModel)) {
            this.formGroup.patchValue({
                docType: this.docBoxModel.docType,
                title: this.docBoxModel.title,
            });
        }

        this.formGroup.patchValue({
            orderNumber: this.notification.identifier.qmNum, // in FI the notification number is being filled with the order number
            // should be prefilled with FINAL
            reportStatus: FormsConstants.FINAL,
        });

        this.formGroup
            .get('file')
            ?.valueChanges.pipe(takeUntil(this.onDestroy$))
            .subscribe((file: File) => {
                if (isDefined(file) && this.formGroup.get('file')?.valid) {
                    this.formGroup.patchValue({ protectionClass: 'Internal' }, { emitEvent: false });
                    if (file.type === 'application/pdf') {
                        this.formGroup.get('protectionClass')?.enable({ emitEvent: false });
                    } else {
                        this.formGroup.get('protectionClass')?.disable({ emitEvent: false });
                    }
                    this.formGroup.patchValue({ title: `${this.getTitle()}-${file.name}` }, { emitEvent: false });
                }
            });
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    private getTitle(): string {
        const materialNumber = this.notification.equipmentSet[0].matnr;
        const serialNumber = this.notification.equipmentSet[0].sernr;
        const equipmentDescription = this.notification.equipmentSet[0].equiShortDesc;
        return `${materialNumber}-${serialNumber}-${equipmentDescription}`;
    }

    public upload(): void {
        const document = this.formGroup.getRawValue() as DocumentFormStateUpload;
        const reportStatusValue = this.formGroup.get('reportStatus')?.value;
        const reportStatus =
            reportStatusValue === FormsConstants.FINAL ? DocBoxReportStatus.FINAL : DocBoxReportStatus.INTERIM;

        // we just need to pass the printNumber.
        // We shouldn't give the user the possibility to manually edit it
        const documentWithPrintNumber: DocumentStateUpload = {
            ...document,
            printNumber: this.docBoxModel ? this.docBoxModel.printNumber : '',
            funcLoc: this.notification.functionalLocation,
            equipment: this.notification.equipmentSet[0].equnr,
            protectionClass:
                document.protectionClass === FormsConstants.INTERNAL
                    ? FormsConstants.RESTRICTED
                    : FormsConstants.UNRESTRICTED,
            reportStatus,
        };
        this.uploadPending = true;
        this.showErrorMessage = false;
        this.docBoxService
            .uploadDocBoxFile(documentWithPrintNumber, this.notification)
            .pipe(
                take(1),
                finalize(() => (this.uploadPending = false)),
            )
            .subscribe(
                () => {
                    this.modalResult.next(document.title);
                    this.modalResult.complete();
                    this.showSuccessMessage = true;
                },
                () => {
                    this.showErrorMessage = true;
                },
            );
    }

    public closeModal(): void {
        this.modalResult.next('');
        this.modalResult.complete();
        this.modalInstance.hide();
    }
}
